import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaCheckCircle, FaCoins } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import AttendanceComponent from './AttendanceComponent';
import Button from './Button';
import { getEventAttendees } from '../../utils/eventUtils';
import { Event, EventParticipationAnswer } from '../../types/event';
import { respondToEvent } from '../../services/eventService';
import { getUserId, hasToken } from '../../services/httpService';

interface EventCardProps {
  event: Event;
  className?: string;
  onEdit?: (eventId: number) => void;
}

export default function EventCard({ event, className, onEdit }: EventCardProps) {
  const { t } = useTranslation();
  const [selectedAnswer, setSelectedAnswer] = useState<EventParticipationAnswer | null>(null);

  useEffect(() => {
    if (!hasToken()) return;
    try {
      const userId = getUserId();
      const participant = event.participants?.find((p) => p.id === userId);
      setSelectedAnswer((participant && participant.answer) || null);
    } catch (e) {
      // ignore if token invalid
    }
  }, [event]);

  const numberOfAttendees = getEventAttendees(event as any);
  const maximumAttendeesReached = event.maximumAttendees ? numberOfAttendees >= event.maximumAttendees : false;

  const handleSetSelectedAnswer = async (answer: EventParticipationAnswer) => {
    try {
      await respondToEvent(event.id.toString(), answer);
      setSelectedAnswer(answer);
    } catch (error) {
      console.error('Failed to update RSVP:', error);
    }
  };

  const navigate = useNavigate();

  const openEventModal = () => {
    try {
      if (event.communityId) {
        navigate('/community/events', { state: { communityId: event.communityId, openEventId: event.id } });
      } else {
        navigate('/agenda', { state: { openEventId: event.id } });
      }
    } catch (e) {
      console.error('Failed to open event modal', e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openEventModal()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openEventModal();
        }
      }}
      className={`hover:bg-secondary-100 relative flex w-full transform flex-col items-start gap-3 rounded-md bg-secondary-50 p-3 transition-transform hover:-translate-y-1 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary sm:flex-row sm:items-center sm:p-3 ${className || ''}`}>
      {onEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(event.id);
          }}
          aria-label={t('general.edit')}
          className="absolute right-3 top-3 z-10 rounded-md bg-secondary-50 p-1">
          <FiEdit2 className="h-4 w-4 text-primary" />
        </button>
      )}

      {event.picture ? (
        <img
          src={event.picture}
          alt={event.title}
          className="mx-auto h-14 w-14 flex-shrink-0 rounded-lg object-cover sm:mx-0 sm:h-12 sm:w-12"
        />
      ) : (
        <div className="mx-auto flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 sm:mx-0 sm:h-12 sm:w-12">
          <FaCalendarAlt className="h-8 w-8 text-primary sm:h-6 sm:w-6" />
        </div>
      )}

      <div className="w-full min-w-0 flex-1 text-left">
        <p className="truncate font-medium text-primary">{event.title}</p>
        <p className="mt-0.5 text-sm text-details/80">{new Date(event.startTime).toLocaleString()}</p>
        <p className="text-sm text-details/80">{event.location || '-'}</p>
      </div>

      <div
        className="mt-2 flex w-full items-center justify-center sm:ml-auto sm:mt-0 sm:w-auto sm:justify-end"
        onClick={(e) => e.stopPropagation()}>
        {hasToken() ? (
          event.priceOfTicket ? (
            selectedAnswer === EventParticipationAnswer.YES ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">
                <FaCheckCircle className="h-4 w-4" />
                {t('page.payment.status.PAID')}
              </span>
            ) : (
              <Button
                variant="primary"
                className="inline-flex w-full items-center justify-center gap-2 text-sm sm:w-auto sm:text-base"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/event/invite`, { state: { eventId: event.id } });
                }}
                aria-label={t('page.events.invite.buyTicket')}>
                <FaCoins className="h-4 w-4" />
                {t('page.events.invite.buyTicket')}
              </Button>
            )
          ) : (
            <div className="flex items-center justify-end">
              <div className="flex items-center">
                <AttendanceComponent
                  selectedAnswer={selectedAnswer}
                  setSelectedAnswer={(answer) => handleSetSelectedAnswer(answer)}
                  disableYes={maximumAttendeesReached}
                />
              </div>
            </div>
          )
        ) : (
          <div className="text-sm text-details/80">{event.participants?.length || 0}</div>
        )}
      </div>
    </div>
  );
}
